/**
 * Main App component with Clerk authentication
 */

import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import {
  SignedOut,
  RedirectToSignIn,
  useAuth as useClerkAuth,
} from '@clerk/clerk-react';
import { useAuthContext } from './contexts/AuthContext';
import { Header } from './components/layout/Header';
import SignInPage from './pages/auth/SignInPage';
import SignUpPage from './pages/auth/SignUpPage';
import ApiKeysPage from './pages/settings/ApiKeysPage';
import TasksPage from './pages/TasksPage';
import EvaluationsPage from './pages/EvaluationsPage';
import ModelsPage from './pages/ModelsPage';
import './index.css';
import { HomeWithHeader } from './components/HomeWithHeader';

function Settings() {
  return (
    <div className='min-h-screen bg-gray-50'>
      <Header />
      <main className='w-full py-6 sm:px-6 lg:px-8'>
        <div className='px-4 py-6 sm:px-0'>
          <ApiKeysPage />
        </div>
      </main>
    </div>
  );
}

// Loading component with improved styling
function LoadingScreen() {
  return (
    <div className='min-h-screen flex items-center justify-center bg-gray-50'>
      <div className='text-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4'></div>
        <p className='text-gray-600'>Cargando autenticación...</p>
        <p className='text-sm text-gray-400 mt-2'>
          Sincronizando con el backend
        </p>
      </div>
    </div>
  );
}

// Error component for auth errors
function AuthErrorScreen({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) {
  return (
    <div className='min-h-screen flex items-center justify-center bg-gray-50'>
      <div className='text-center max-w-md mx-auto p-6'>
        <div className='text-red-500 mb-4'>
          <svg
            className='h-12 w-12 mx-auto'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z'
            />
          </svg>
        </div>
        <h2 className='text-xl font-semibold text-gray-900 mb-2'>
          Error de Autenticación
        </h2>
        <p className='text-gray-600 mb-4'>{error}</p>
        <button
          onClick={onRetry}
          className='bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors'
        >
          Reintentar
        </button>
      </div>
    </div>
  );
}

// Protected route wrapper that integrates with both Clerk and backend auth
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useClerkAuth();
  const { isLoading, isAuthenticated, error, reauthenticate } =
    useAuthContext();

  // Show loading while Clerk is initializing
  if (!isLoaded) {
    return <LoadingScreen />;
  }

  // If user is not signed in to Clerk, redirect to sign-in
  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  // Show loading while backend auth is being processed
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Show error if there was an auth error
  if (error) {
    return <AuthErrorScreen error={error} onRetry={reauthenticate} />;
  }

  // If user is not authenticated with backend, show error
  if (!isAuthenticated) {
    return (
      <AuthErrorScreen
        error='No se pudo autenticar con el backend. Verifica tu conexión e intenta nuevamente.'
        onRetry={reauthenticate}
      />
    );
  }

  // User is fully authenticated, show the protected content
  return <>{children}</>;
}

// Root redirect component that checks auth status
function RootRedirect() {
  const { isLoaded, isSignedIn } = useClerkAuth();
  const { isAuthenticated } = useAuthContext();

  // Show loading while Clerk is initializing
  if (!isLoaded) {
    return <LoadingScreen />;
  }

  // If user is signed in and authenticated, redirect to dashboard
  if (isSignedIn && isAuthenticated) {
    return <Navigate to='/dashboard' replace />;
  }

  // If user is not signed in, redirect to sign-in
  return <Navigate to='/auth/sign-in' replace />;
}

function AppRoutes() {
  return (
    <Router>
      <Routes>
        {/* Public auth routes */}
        <Route
          path='/auth/sign-in/*'
          element={
            <SignedOut>
              <SignInPage />
            </SignedOut>
          }
        />
        <Route
          path='/auth/sign-up/*'
          element={
            <SignedOut>
              <SignUpPage />
            </SignedOut>
          }
        />

        {/* Protected routes */}
        <Route
          path='/dashboard'
          element={
            <ProtectedRoute>
              <HomeWithHeader />
            </ProtectedRoute>
          }
        />
        <Route
          path='/evaluations'
          element={
            <ProtectedRoute>
              <EvaluationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/tasks'
          element={
            <ProtectedRoute>
              <TasksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/models'
          element={
            <ProtectedRoute>
              <ModelsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/settings'
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        {/* Root redirect */}
        <Route path='/' element={<RootRedirect />} />

        {/* Fallback for unknown routes */}
        <Route path='*' element={<Navigate to='/dashboard' replace />} />
      </Routes>
    </Router>
  );
}

export default function App() {
  return <AppRoutes />;
}
